/* eslint-disable no-use-before-define */
// ---------------------------------------------------------------------------
// MIGRATION NOTE — future direction for this context
// ---------------------------------------------------------------------------
// DataModelContext is built around the legacy flat-blob page data model:
// hierarchyId tree traversal, PageModule blobs, groupId strings on modules.
//
// The new data model (groups-based, used by layouts) is implemented in
// src/contexts/layout-edit/LayoutEditContext.tsx. That context is the
// intended pattern going forward:
//   - single source of truth: layout.groups[].modules[] (slot refs, not blobs)
//   - dirty state via last-saved snapshot + updateLastSavedState()
//   - no hierarchyId traversal — direct array mutation by groupId
//
// When pages migrate to the groups-based shape (Release C), this context
// should be refactored to follow the LayoutEditContext pattern. At that
// point, the hierarchyId tree, renderEntity, and multizone logic here can
// be removed, and both pages and layouts will share the same data model.
// ---------------------------------------------------------------------------
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Button, IconArrowDown, IconArrowUp, IconHandle, Wrapper } from '@screentone/core';
import { cloneDeep, groupBy, isEqual, isEqualWith } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { Group } from 'components/datamodel/container/Group/Group';
import { Issue } from 'components/datamodel/container/Issue/Issue';
import { IssueSection } from 'components/datamodel/container/Issue/subtypes/IssueSection';
import { List } from 'components/datamodel/container/List/List';
import { NewswiresList } from 'components/datamodel/container/List/subtypes/NewswiresList/NewswiresList';
import { BannerModule } from 'components/datamodel/container/Module/BannerModule/BannerModule';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { Module } from 'components/datamodel/container/Module/Module';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MultiZoneModule } from 'components/datamodel/container/Module/MultiZoneModule/MultiZoneModule';
import { MultiList } from 'components/datamodel/container/MultiList/MultiList';
import Newsletter from 'components/datamodel/container/Newsletter/Newsletter';
import OffPlatform from 'components/datamodel/container/OffPlatform/OffPlatform';
import { Page } from 'components/datamodel/container/Page/Page';
import { Section } from 'components/datamodel/container/Section/Section';
import { Article, ArticleProps } from 'components/datamodel/content/Article/Article';
import { Collection } from 'components/datamodel/content/Collection/Collection';
import { CommonContentProps } from 'components/datamodel/content/commonContentProps';
import { LinkedItem as LinkedItemComponent } from 'components/datamodel/content/LinkedItem/LinkedItem';
import { Feed } from 'components/datamodel/content/Query/Feed/Feed';
import { Query } from 'components/datamodel/content/Query/Query';
import { useAlert } from 'contexts/alert/useAlert';
import { DRAGGABLE_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { GroupProvider } from 'contexts/group/GroupContext';
import {
  ArticleItem,
  BannerModuleContainer,
  BaseMetadata,
  CollectionDtoMetadata,
  ContentUnion,
  ExternalCollectionItem,
  GraphicLinkedItem,
  IssueContainer,
  IssueSectionContainer,
  Layout,
  LinkedItem,
  LinkedItemCardType,
  ListContainer,
  Metadata as TitleModuleMetadata,
  MobileArticleItem,
  MobileLinkedItem,
  ModuleContainer,
  ModuleContainerUnion,
  MultiListContainer,
  MultiZoneModuleContainer,
  NewsletterPageContainer,
  NewswiresListContainer,
  OffPlatformListContainer,
  OffPlatformMetadata,
  PageContainer,
  PageModule,
  PageValidation,
  PublicationSettingSearchableContentType,
  QueryItem,
  SectionAttributes,
  SectionContainer,
  StructureGroupConfig,
  StructureSlot,
  TreatmentTypeSetting,
  UiModule,
  ZoneConfig
} from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehSummariesIncludedAttributes } from 'hooks/useAllessehCollectionSummariesQuery';
import { AllessehContent, AllessehContentQueryBody, AllessehQueryRule } from 'hooks/useAllessehContentQuery';
import { MULTIZONE_MODULE } from 'utils/constants';
import { isArticleOrMediaContentType } from 'utils/contentType';
import { reportError } from 'utils/errorBoundary';
import { isGraphicLinkedItem, isMobileLinkedItem, makeMobileArticle } from 'utils/mobile-content';
import { adjustContentIndexes, compareItems, replaceZoneIdentifiers } from 'utils/moduleItems';
import { getModuleFieldKey, getTreatmentType } from 'utils/modules';
import { calculateOverfetchValue, moduleContainsMatchingQuery } from 'utils/overfetch';
import { cleanLayoutContent } from 'utils/pageUtils';
import { safelyParseContent } from 'utils/temp';
import {
  getTotalContentItemCounts,
  isArticleItem,
  isEqualIgnoringVolatileFields,
  isIssueSectionContainer,
  removeDedupedNotRulesFromTree
} from './DataModelContext.utils';
// Need to use relative import for stylesheet, otherwise an error will be thrown when running tests
import moduleStyles from '../../components/datamodel/container/Module/Module.module.scss';

import {
  DEFAULT_GRAPHIC_LINKED_ITEM_ZONE,
  DEFAULT_LINKED_ITEM_ZONE
} from 'components/datamodel/content/LinkedItem/LinkedItem.constants';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { DataModelContext, DataModelStableContext } from 'contexts/datamodel/DataModelContext.core';
import { Structure } from 'utils/structure.utils';

export { DataModelContext, DataModelStableContext } from 'contexts/datamodel/DataModelContext.core';
export type { DataModelStableContextValue } from 'contexts/datamodel/DataModelContext.core';

export const DataModelType = {
  BannerDTO: 'BannerDTO',
  CollectionDTO: 'CollectionDTO',
  ItpDTO: 'ItpDTO',
  NewsletterDTO: 'NewsletterDTO',
  OffPlatformDTO: 'OffPlatformDTO',
  PageDTO: 'PageDTO',
  MultiCollectionDTO: 'MultiCollectionDTO'
} as const;

export type DataModelTypeKeys = keyof typeof DataModelType;

export const EntityType = {
  Article: 'Article',
  Collection: 'Collection',
  Issue: 'Issue',
  LinkedItem: 'LinkedItem',
  List: 'List',
  MultiList: 'MultiList',
  Module: 'Module',
  Page: 'Page',
  Query: 'Query',
  Section: 'Section',
  Group: 'Group'
} as const;

export type Container =
  | MultiListContainer
  | BannerModuleContainer
  | IssueContainer
  | IssueSectionContainer
  | ListContainer
  | ModuleContainer
  | NewsletterPageContainer
  | OffPlatformListContainer
  | NewswiresListContainer
  | PageContainer
  | SectionContainer
  | MultiZoneModuleContainer
  | GroupContainer;
export type CollectionsType = PageContainer | ModuleContainer | ContentUnion;

export type ContentItem =
  | ArticleItem
  | ExternalCollectionItem
  | QueryItem
  | LinkedItem
  | MobileLinkedItem
  | GraphicLinkedItem
  | MobileArticleItem;
export type EntityComponent = Container | ContentItem;
export type Metadata = BaseMetadata | CollectionDtoMetadata | OffPlatformMetadata;

export type GroupContainer = {
  __typename?: 'GroupContainer';
  collection: Array<EntityComponent>;
  type: 'Group';
  subtype?: string;
  groupId?: string;
  isGroupEnabled?: boolean;
};

export type ExtraProps = {
  article?: {
    isOverflow?: boolean;
    renderActions?: ArticleProps['renderActions'];
  };
  content?: CommonContentProps;
  module?: {
    layout?: Layout | undefined;
    treatmentTypeSetting?: TreatmentTypeSetting | undefined;
    canRemoveFromGroup?: boolean;
  };
  group?: {
    groupsItemCount?: number[];
  };
};

type RenderEntityOptions = {
  hierarchyId: string;
  index?: number;
  extraProps?: ExtraProps;
  isHistory?: boolean;
  withoutDragNDrop?: boolean;
  isInEditMode?: boolean;
  isReadOnly?: boolean;
};

export type TraverseEntityOptions = {
  parentHierarchyId?: string;
  entityIndex?: number;
  dontTraverseDisabledGroups?: boolean;
};

type FromAllessehContentOptions = {
  generateUUID?: boolean;
};

export interface IDataModelContext<T extends Container> {
  type: DataModelTypeKeys;
  root: T;
  metadata: Metadata;
  hasModelChangedOnce: boolean;
  hasModelChangedRecently: boolean;
  setHasModelChangedRecently: React.Dispatch<React.SetStateAction<boolean>>;
  resetModelChanged: () => void;
  readHierarchyId: {
    (hierarchyId: string): {
      entity: (typeof EntityType)[keyof typeof EntityType];
      index: number;
    }[];
    (hierarchyId: string, onlyIndices: boolean): number[];
  };
  getAllArticleIds(): string[];
  getAllExternalCollectionIds(): string[];
  getDTO(
    deleteResolvedFields?: boolean,
    shouldDedupe?: boolean
  ): {
    root: Container;
    metadata: Metadata;
    structure?: Structure;
  };
  getGroupConfig(groupId: string): unknown;
  getAllGroupConfigs(): Array<StructureGroupConfig & { id: string }>;
  getRefDTO: {
    (withResolvedRoot: true): {
      type: DataModelTypeKeys;
      childRefs: string[];
      root: Container;
      metadata: Metadata;
    };
    (withResolvedRoot: false): {
      type: DataModelTypeKeys;
      childRefs: string[];
      metadata: Metadata;
    };
  };
  deleteResolvedFieldsFromExternalDTO<T extends Container>(dto: {
    root: T;
    metadata: Metadata;
  }): {
    root: T;
    metadata: Metadata;
  };
  fromAllessehContent(
    content: AllessehContent | AllessehSummariesIncludedAttributes,
    options?: FromAllessehContentOptions
  ): ArticleItem | LinkedItem;
  generateHierarchyId(entity: EntityComponent, parentHierarchyId?: string, index?: number): string;
  generateHierarchyIdFromDragEvent(droppableId: string, dropIndex: number): string;
  setNewRoot(newRoot: Container): void;
  applyDedupedRoot(dedupedRoot: Container): void;
  setNewMetadata(newMetadata: Partial<Metadata>): void;
  getEntity<E extends EntityComponent>(hierarchyId: string, options?: { indices?: number[]; root?: E }): E | null;
  swapEntities(srcHierarchyId: string, destHierarchyId: string): void;
  insertEntity(destHierarchyId: string, entity: EntityComponent | EntityComponent[]): boolean;
  modifyEntity(
    hierarchyId: string,
    callback: (entity: EntityComponent) => void,
    params?: { updateRoot?: boolean }
  ): void;
  clearModule(hierarchyId: string): void;
  modifyAllByType<E extends EntityComponent = EntityComponent>(type: string, callback: (entity: E) => void): void;
  applyOverfetchForModifiedQuery(queryId: string, query: Record<string, unknown>): void;
  applyOverfetchForAddedQuery(
    moduleHierarchyId: string,
    query: Record<string, unknown>,
    queryIdToExclude?: string
  ): PageModule | null;
  insertEntityToCollection(destHierarchyId: string, entity: ModuleContainerUnion): void;
  changeEntity(hierarchyId: string, callback: (oldEntity: EntityComponent) => EntityComponent): void;
  removeEntity(hierarchyId: string, cleanupDedupe?: boolean): EntityComponent | null;
  removeAllByType(type: string): void;
  cleanupDedupeForGroup(moduleHierarchyIds: string[]): void;
  getEmbargoContent(): ArticleItem[];
  getCancelledEmbargoContent(): ArticleItem[];
  getSortedEmbargoContent(sort: 'asc' | 'desc'): AllessehContent[];
  renderEntity(entity: EntityComponent, options: RenderEntityOptions): React.ReactElement;
  isEditingLinkedItem: Set<string>;
  setIsEditingLinkedItem: React.Dispatch<React.SetStateAction<Set<string>>>;
  isItemInDataModel(item: ContentItem): boolean;
  countContent: (entity: EntityComponent) => string;
  getAllModuleCounts: () => Record<string, number>;
  traverseEntity(
    entity: EntityComponent,
    callback: (entity: EntityComponent, hierarchyId: string) => void,
    options?: TraverseEntityOptions
  ): void;
  setIsQueryLoading: React.Dispatch<React.SetStateAction<boolean>>;
  queriesHaveSettled: boolean;
  getRootCopy: () => Container;
  setHasModelChangedOnce: React.Dispatch<React.SetStateAction<boolean>>;
  getArticlePromotionLogByOriginId: (originId?: string | null) => ArticlePromotionLogData | null;
  getSectionAttributes: () => SectionAttributes;
  setSectionAttributes: (newAttributes: SectionAttributes) => void;
  updateLastSavedState: () => void;
  isReadOnly: boolean;
  validations: PageValidation[];
}

export type ArticlePromotionLogData = {
  promotedAt: number;
};

export type ArticlePromotionLog = Record<string, ArticlePromotionLogData> | null;

interface DataModelProviderProps {
  children: React.ReactNode;
  metadata: Metadata;
  root: Container;
  setHasItpDTOChanged?: (hasChanged: boolean) => void;
  type: DataModelTypeKeys;
  articlePromotionLog?: ArticlePromotionLog;
  shouldCleanContent?: boolean;
  childRefs?: string[];
  isReadOnly?: boolean;
  validations?: PageValidation[];
  structure?: Structure | null;
}

// DataModelContext and DataModelStableContext are now imported from DataModelContext.core
// so that useDataModelContext (useDataModel.ts) and DataModelProvider share the same context instance.

function readHierarchyId(
  hierarchyId: string
): { entity: (typeof EntityType)[keyof typeof EntityType]; index: number }[];
function readHierarchyId(hierarchyId: string, onlyIndices: boolean): number[];
function readHierarchyId(hierarchyId: string, onlyIndices = false) {
  if (onlyIndices) {
    const stringIndices = hierarchyId.match(/\d+/g);
    if (!stringIndices) {
      throw new Error(`No indices found in hierarchyId: ${hierarchyId}`);
    }

    return stringIndices.map((index) => parseInt(index, 10));
  }

  const splitHierarchyId = hierarchyId.split('_').map((part) => part.split('-'));
  return splitHierarchyId.map(([entity, index]) => {
    // Convert the lowercased entity type to EntityType enum
    const entityType = (entity[0].toUpperCase() + entity.slice(1)) as (typeof EntityType)[keyof typeof EntityType];
    return { entity: entityType, index: parseInt(index, 10) };
  });
}

const isPopulatedLinkedItem = (item: EntityComponent) => {
  if (item.type !== EntityType.LinkedItem) {
    return true;
  }
  const attrs = (item as LinkedItem).attributes;
  return !!attrs.id && !!attrs.hosted_url;
};

// Recursively removes linked items with no id/url from every collection in the tree.
// For MultiZoneModuleContainers, zones.contentIndexes reference positions in collection,
// so removing an item would leave stale indexes pointing at undefined — the backend calls
// toAllesseh() on those and crashes. We track which indexes are removed before filtering
// and update zones afterward to keep them in sync.
const stripEmptyLinkedItemsFromCollection = (items: EntityComponent[]): EntityComponent[] =>
  items.filter(isPopulatedLinkedItem).map((item) => {
    if (!('collection' in item)) {
      return item;
    }

    const nested = item as EntityComponent & { collection: EntityComponent[] };
    const removedIndexes = nested.collection.reduce<number[]>((acc, child, i) => {
      if (!isPopulatedLinkedItem(child)) {
        acc.push(i);
      }
      return acc;
    }, []);

    nested.collection = stripEmptyLinkedItemsFromCollection(nested.collection);
    if (removedIndexes.length > 0) {
      const multizone = item as MultiZoneModuleContainer;
      if (multizone.attributes.zones) {
        // Process in descending order so earlier removals don't shift subsequent indexes
        [...removedIndexes].reverse().forEach((removedIndex) => {
          adjustContentIndexes(multizone.attributes.zones as ZoneConfig[], removedIndex);
        });
      }
    }
    return item;
  });

export const DataModelProvider = ({
  children,
  metadata: initialMetadata,
  root: initialRoot,
  setHasItpDTOChanged,
  type,
  articlePromotionLog: initialArticlePromotionLog,
  shouldCleanContent = false,
  childRefs: initialChildRefs = [],
  isReadOnly = false,
  validations: initialValidations,
  structure: initialStructure
}: DataModelProviderProps) => {
  const processedRoot = useMemo(() => {
    const base = shouldCleanContent ? cleanLayoutContent(initialRoot) : initialRoot;
    const root = cloneDeep(base);
    // The backend does not persist uuid on LinkedItems. Assign one here so
    // isEditingLinkedItem can track open forms by stable identity within the session.
    const assignMissingUuids = (entity: EntityComponent) => {
      if (entity.type === EntityType.LinkedItem && !entity.uuid) {
        // eslint-disable-next-line no-param-reassign
        entity.uuid = uuidv4();
      }
      if ('collection' in entity) {
        (entity.collection as EntityComponent[]).forEach(assignMissingUuids);
      }
    };
    assignMissingUuids(root);

    return root;
  }, [initialRoot, shouldCleanContent]);

  const [root, setRoot] = useState(processedRoot);
  const [metadata, setMetadata] = useState(initialMetadata);
  const [articlePromotionLog, setArticlePromotionLog] = useState(initialArticlePromotionLog);
  // structure is stored in a ref rather than state so reads (getGroupConfig, getAllGroupConfigs)
  // are always current without causing re-renders. It is structure-only — old-shape pubs pass nothing here
  // and both accessors return undefined / []. A useEffect keeps the ref in sync when the page loads.
  const structureRef = useRef<Structure | null | undefined>(initialStructure);
  useEffect(() => {
    structureRef.current = initialStructure;
  }, [initialStructure]);
  // Returns the group config (allowedTreatmentTypes, min, max, etc.) for a given groupId.
  // Returns undefined for pubs that don't use structure (old data shape).
  const getGroupConfig = useCallback(
    (groupId: string) => structureRef.current?.groups.find((g) => g.id === groupId)?.config,
    []
  );
  // Returns all group configs as a flat list with their groupId included.
  // Returns [] for pubs that don't use structure (old data shape).
  const getAllGroupConfigs = useCallback(
    (): Array<StructureGroupConfig & { id: string }> =>
      structureRef.current?.groups.map((g) => ({ id: g.id, ...g.config })) ?? [],
    []
  );
  const [validations] = useState<PageValidation[]>(initialValidations ?? []);
  const [hasModelChangedOnce, setHasModelChangedOnce] = useState(false);
  const [hasModelChangedRecently, setHasModelChangedRecently] = useState(false);
  const [isEditingLinkedItem, setIsEditingLinkedItem] = useState<Set<string>>(new Set());
  const [isQueryLoading, setIsQueryLoading] = useState(false);
  const [queriesHaveSettled, setQueriesHaveSettled] = useState(false);
  const [lastSavedRoot, setLastSavedRoot] = useState(processedRoot);
  const [lastSavedMetadata, setLastSavedMetadata] = useState(initialMetadata);
  const { data } = usePublicationSettings();
  const { alertError } = useAlert();

  const auxRoot = useRef(processedRoot); // auxiliary root to apply successive changes to the root, bypassing React automatic state batching

  // Stable reference: reads auxRoot via a ref so the empty dependency array is safe —
  // it never captures a stale root value.
  const getEntity = useCallback(
    (
      hierarchyId: string,
      { indices, root: rootArg }: { indices?: number[]; root?: EntityComponent } = {}
    ): EntityComponent | null => {
      const hierarchyIdSanitized = hierarchyId.replace(/.+?#/, '');
      const hierarchyIdIndices = indices ?? readHierarchyId(hierarchyIdSanitized, true);

      return (
        hierarchyIdIndices.reduce((entity: EntityComponent | null, index): EntityComponent | null => {
          if (!entity) return rootArg ?? (auxRoot.current as EntityComponent);

          if (
            entity.type === EntityType.Module &&
            entity.subtype === MULTIZONE_MODULE &&
            'zones' in entity.attributes
          ) {
            return entity;
          }

          if ('collection' in entity) {
            return entity.collection[index] as EntityComponent;
          }
          return null;
        }, null) ?? null
      );
    },
    []
  ) as IDataModelContext<Container>['getEntity'];

  const getArticlePromotionLogByOriginId = useCallback(
    (originId?: string | null) => {
      if (!articlePromotionLog || !originId) return null;

      return articlePromotionLog[originId];
    },
    [articlePromotionLog]
  );

  const setNewRoot = useCallback((newRoot: Container) => {
    if (isEqual(newRoot, auxRoot.current)) {
      return;
    }
    auxRoot.current = newRoot;

    setHasModelChangedRecently(true);
    setRoot(newRoot);
  }, []);

  // Preserve contentItems from collections and pageModule overrides before applying dedupe result.
  // The backend may not echo back minItemsOverride/maxItemsOverride, so we carry them forward
  // from the current in-memory root to prevent the UI from snapping back to defaults.
  const applyDedupedRoot = useCallback(
    (dedupedRoot: Container) => {
      const currentRoot = cloneDeep(auxRoot.current);
      const dedupedRootCopy = cloneDeep(dedupedRoot);

      const preserveFields = (currentEntity: EntityComponent, dedupedEntity: EntityComponent): void => {
        if (dedupedEntity.type === EntityType.Collection) {
          const currentCollection = currentEntity as ExternalCollectionItem;
          const dedupedCollection = dedupedEntity as ExternalCollectionItem;

          const hasEmptyContentItems =
            Array.isArray(dedupedCollection.contentItems) && dedupedCollection.contentItems.length === 0;

          const hasLoadedContentItems =
            Array.isArray(currentCollection.contentItems) && currentCollection.contentItems.length > 0;

          if (hasEmptyContentItems && hasLoadedContentItems) {
            dedupedCollection.contentItems = currentCollection.contentItems;
          }
        }

        if (dedupedEntity.type === EntityType.Module) {
          const currentModule = currentEntity as ModuleContainer;
          const dedupedModule = dedupedEntity as ModuleContainer;
          const currentPageModule = currentModule.attributes.pageModule as PageModule | undefined;
          const dedupedPageModule = dedupedModule.attributes.pageModule as PageModule | undefined;

          if (currentPageModule && dedupedPageModule) {
            if (currentPageModule.minItemsOverride != null && dedupedPageModule.minItemsOverride == null) {
              dedupedPageModule.minItemsOverride = currentPageModule.minItemsOverride;
            }
            if (currentPageModule.maxItemsOverride != null && dedupedPageModule.maxItemsOverride == null) {
              dedupedPageModule.maxItemsOverride = currentPageModule.maxItemsOverride;
            }
          }

          // Preserve unsaved linked item placeholders while the form is open — the deduped root
          // never has them. For multizone, also update contentIndexes so the item is in its zone.
          if ('collection' in currentModule && 'collection' in dedupedModule) {
            const unsavedLinkedItems = currentModule.collection.filter(
              (item) => item.type === EntityType.LinkedItem && !isPopulatedLinkedItem(item)
            );
            if (unsavedLinkedItems.length > 0) {
              const isMultizone = dedupedModule.subtype === MULTIZONE_MODULE;
              if (isMultizone) {
                const multizone = dedupedModule as unknown as MultiZoneModuleContainer;
                const defaultZoneName =
                  data?.publicationSetting.defaultLinkedItemType === LinkedItemCardType.Graphics
                    ? DEFAULT_GRAPHIC_LINKED_ITEM_ZONE
                    : DEFAULT_LINKED_ITEM_ZONE;
                const zoneIndex = (multizone.attributes.zones ?? []).findIndex((z) => z.name === defaultZoneName);
                unsavedLinkedItems.forEach((item) => {
                  const insertedIndex = multizone.collection.length;
                  multizone.collection.push(item as unknown as ContentUnion);
                  if (zoneIndex !== -1) {
                    const zone = multizone.attributes.zones![zoneIndex];
                    zone.contentIndexes = [...(zone.contentIndexes ?? []), insertedIndex];
                  }
                });
              } else {
                dedupedModule.collection.push(...unsavedLinkedItems);
              }
            }
          }
        }

        // uuid is a frontend-only field not in the GraphQL type — preserve it across dedupe merges
        if (dedupedEntity.type === EntityType.LinkedItem && !dedupedEntity.uuid) {
          const currentUuid = (currentEntity as { uuid?: string }).uuid;
          if (currentUuid) {
            // eslint-disable-next-line no-param-reassign
            dedupedEntity.uuid = currentUuid;
          }
        }

        // Recursively traverse collections
        if ('collection' in dedupedEntity && Array.isArray(dedupedEntity.collection)) {
          dedupedEntity.collection.forEach((dedupedChild, index) => {
            if ('collection' in currentEntity && index < currentEntity.collection.length) {
              preserveFields(currentEntity.collection[index], dedupedChild);
            }
          });
        }
      };

      preserveFields(currentRoot, dedupedRootCopy);

      // Preserve current root attributes. The artifactDTO memo in Query/Feed is keyed on
      // root.collection, so section-attribute edits don't refresh it; a dedup request fired
      // after such an edit carries stale attributes, which the backend echoes back. Without
      // this, applyDedupedRoot would silently overwrite the user's in-progress edits.
      if ('attributes' in dedupedRootCopy && 'attributes' in currentRoot) {
        (dedupedRootCopy as SectionContainer).attributes = (currentRoot as SectionContainer).attributes;
      }

      setNewRoot(dedupedRootCopy);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setNewRoot]
  );

  function isOffPlatformMetadata(metadata: Metadata): metadata is OffPlatformMetadata {
    return 'isArchived' in metadata;
  }

  function setNewMetadata(newMetadata: Partial<Metadata>) {
    setMetadata((prev) => {
      if (isOffPlatformMetadata(prev)) {
        return { ...prev, ...newMetadata } as OffPlatformMetadata;
      }

      return { ...prev, ...newMetadata } as BaseMetadata;
    });
  }

  function resetModelChanged() {
    setHasModelChangedRecently(false);
    setHasModelChangedOnce(false);
  }

  function updateLastSavedState() {
    setLastSavedRoot(cloneDeep(auxRoot.current));
    setLastSavedMetadata(cloneDeep(metadata));
    resetModelChanged();
  }

  const resetState = useCallback(() => {
    auxRoot.current = processedRoot;
    setRoot(processedRoot);
    setMetadata(initialMetadata);
    setArticlePromotionLog(initialArticlePromotionLog);
  }, [processedRoot, initialMetadata, initialArticlePromotionLog]);

  // Stable reference: depends only on getEntity, which is itself stable (see above).
  // This means generateHierarchyId's identity doesn't change when root changes.
  const generateHierarchyId = useCallback(
    (entity: EntityComponent, parentHierarchyId?: string, index?: number): string => {
      let hierarchyId = entity.type.toLowerCase();

      if (parentHierarchyId) {
        hierarchyId = `${parentHierarchyId}_${hierarchyId}`;

        const module = getEntity<ModuleContainerUnion>(parentHierarchyId);

        if (module?.type === EntityType.Module && module.subtype === MULTIZONE_MODULE) {
          const multiZoneModule = module as MultiZoneModuleContainer;
          const entityId = (entity as ContentItem).attributes.id;
          const entityUuid = (entity as { uuid?: string }).uuid;
          const itemIndex =
            index ??
            multiZoneModule.collection.findIndex((contentItem) => {
              if (entityId) {
                return contentItem.attributes.id === entityId;
              }
              return (contentItem as { uuid?: string }).uuid === entityUuid;
            });

          multiZoneModule.attributes.zones?.forEach((zone, zoneIndex) => {
            if (itemIndex !== -1 && itemIndex < zone.contentIndexes!.length) {
              const contentIndex = zone.contentIndexes![itemIndex];
              const collectionItem = multiZoneModule.collection[contentIndex];
              const matches = entityId
                ? collectionItem.attributes.id === entityId
                : (collectionItem as { uuid?: string } | undefined)?.uuid === entityUuid;
              if (matches) {
                hierarchyId = `${parentHierarchyId}_zone-${zoneIndex}_${entity.type.toLowerCase()}-${itemIndex}`;
              }
            }
          });

          return hierarchyId;
        }
      }

      if (index !== undefined) {
        hierarchyId = `${hierarchyId}-${index}`;
      }

      return hierarchyId;
    },
    [getEntity]
  );

  function generateHierarchyIdFromDragEvent(droppableId: string, dropIndex: number) {
    if (droppableId.startsWith('GROUP#')) {
      return `${droppableId.replace(/GROUP#/, '').replace(/_group-\d+/, '')}_entity-${dropIndex}`;
    }
    return `${droppableId}_entity-${dropIndex}`;
  }

  function traverseEntity(
    entity: EntityComponent,
    callback: (entity: EntityComponent, hierarchyId: string) => void,
    options: TraverseEntityOptions = { parentHierarchyId: '', entityIndex: 0, dontTraverseDisabledGroups: false }
  ) {
    const { parentHierarchyId = '', entityIndex = 0, dontTraverseDisabledGroups = false } = options;

    const hierarchyId = generateHierarchyId(entity, parentHierarchyId, entityIndex);
    callback(entity, hierarchyId);

    const recursiveOptions = {
      parentHierarchyId: hierarchyId,
      dontTraverseDisabledGroups
    };
    if ('collection' in entity) {
      entity.collection.forEach((child, i) => {
        // Skip this child if it's a disabled group and skipping is enabled
        const isDisabledGroup = 'isGroupEnabled' in child && !!child.groupId && child.isGroupEnabled === false;

        if (isDisabledGroup && dontTraverseDisabledGroups) {
          return;
        }

        traverseEntity(child, callback, { ...recursiveOptions, entityIndex: i });
      });
    }
  }

  function getAllArticleIds(): string[] {
    const ids: string[] = [];

    traverseEntity(auxRoot.current, (entity) => {
      if (entity.type === EntityType.Article) {
        const articleItem = entity as ArticleItem;
        ids.push(articleItem.attributes.id);
      }
    });

    return ids;
  }

  function getAllExternalCollectionIds(): string[] {
    const ids: string[] = [];

    traverseEntity(auxRoot.current, (entity) => {
      if (entity.type === EntityType.Collection) {
        const externalCollectionItem = entity as ExternalCollectionItem;
        ids.push(externalCollectionItem.attributes.id);
      }
    });

    return ids;
  }

  function getDTO(deleteResolvedFields = false, shouldDedupe = false) {
    const rootCopy = cloneDeep(auxRoot.current);
    const metadataCopy = cloneDeep(metadata);

    rootCopy.collection = stripEmptyLinkedItemsFromCollection(rootCopy.collection);

    traverseEntity(rootCopy, (entity) => {
      // CLEANUP(structure-migration): remove once all pages use the structure format.
      // Old-shape pages never had slotRef on modules. The group reconstruction in renderEntity can
      // stamp slotRef: null onto old-shape modules as a side effect. Strip it before saving so the
      // stored payload stays in the original shape — adding a null key would silently change the data.
      if (entity.type === EntityType.Module) {
        const mod = entity as unknown as Record<string, unknown>;
        if ('slotRef' in mod && mod.slotRef == null) {
          delete mod.slotRef;
        }
      }

      if (entity.type === EntityType.Module && deleteResolvedFields) {
        const moduleContainer = entity as ModuleContainer;
        const { pageModule } = moduleContainer.attributes;

        if (pageModule) {
          // TODO: Find a way to make this conversion in the backend, so we don't rely on the frontend to do it
          // The following code is a workaround to convert the values of the `contentTypes` and `allowedMeta` properties into the correct format expected by the backend.
          // This is because the frontend uses the enums from the autogenerated GraphQL types, which are PascalCase,
          // while the backend uses its own enums, which are all lowercase for the `contentTypes` property, and camelCase for the `allowedMeta` property.
          //
          // The following commits are related to this issue:
          // - 642e1971e86f40d4c15a5910345a4cf8d3e7be58 - frontend
          // - 8c0f36d9a7e20404c9bf81b7d78b0a0bac4bff3c - backend
          // ===============================================
          // - 8e92f45c5258168461ddab57aa5a037ae373db03 - backend
          if (!pageModule.uiModuleType) return;
          let moduleType = pageModule.uiModuleType.replace(/^Ui|Type$/g, '') as keyof UiModule;
          moduleType = (moduleType[0].toLowerCase() + moduleType.slice(1)) as keyof UiModule;

          const moduleFields = pageModule.uiModuleFields?.[moduleType];
          if (moduleFields && typeof moduleFields === 'object') {
            if ('contentTypes' in moduleFields && moduleFields.contentTypes) {
              moduleFields.contentTypes = moduleFields.contentTypes.map(
                (contentType) => contentType.toLowerCase() as PublicationSettingSearchableContentType
              );
            }

            if ('allowedMeta' in moduleFields && moduleFields.allowedMeta) {
              moduleFields.allowedMeta = moduleFields.allowedMeta.map(
                (meta) => (meta[0].toLowerCase() + meta.slice(1)) as TitleModuleMetadata
              );
            }
          }
        }
      }

      if (entity.type === EntityType.Article && deleteResolvedFields) {
        const articleItem = entity as ArticleItem;
        delete articleItem.content;
      }

      if (entity.type === EntityType.Collection && deleteResolvedFields) {
        const collectionItem = entity as ExternalCollectionItem;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete collectionItem.contentItems;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete collectionItem.metadata;
      }

      if (entity.type === EntityType.Query && deleteResolvedFields) {
        const queryItem = entity as QueryItem;
        delete queryItem.queryItems;
      }
    });

    if (shouldDedupe) {
      dedupe(rootCopy);
    }

    if (structureRef.current) {
      const structureCopy = cloneDeep(structureRef.current);

      // rootCopy.collection[0] is the page section; its collection is the flat module list.
      // ModuleContainer already has slotRef, groupId, type, and attributes.pageModule — no local type needed.
      const flatModules = (rootCopy.collection[0] as { collection: ModuleContainer[] }).collection;

      // Rebuild slot ordering from current root module order so editor reordering is preserved.
      // The BE's extractStructureFromRoot diffs uiModuleFields against TT defaults and stores
      // only the overrides — the FE sends root modules with fully-resolved fields as-is.
      structureCopy.groups.forEach((group) => {
        const groupModules = flatModules.filter((m) => m.type === EntityType.Module && m.groupId === group.id);

        // eslint-disable-next-line no-param-reassign
        group.modules = groupModules.map((m) => {
          const slotRef = m.slotRef ?? '';
          const ttKey = slotRef.replace(/-\d+$/, '');
          const existing = group.modules.find((s) => s.slotRef === slotRef);
          return { ...(existing ?? {}), slotRef, ttKey } as StructureSlot;
        });
      });

      return {
        type,
        root: rootCopy,
        metadata: metadataCopy,
        structure: structureCopy
      };
    }

    return {
      type,
      root: rootCopy,
      metadata: metadataCopy
    };
  }

  function getRefDTO(withResolvedRoot: true): {
    type: DataModelTypeKeys;
    childRefs: string[];
    root: Container;
    metadata: Metadata;
  };
  function getRefDTO(withResolvedRoot: false): {
    type: DataModelTypeKeys;
    childRefs: string[];
    metadata: Metadata;
  };
  function getRefDTO(withResolvedRoot: boolean) {
    const childRefsCopy = cloneDeep(initialChildRefs);
    const metadataCopy = cloneDeep(metadata);

    if (withResolvedRoot) {
      const dto = getDTO(true);
      return {
        type,
        childRefs: childRefsCopy,
        root: dto.root,
        metadata: dto.metadata
      };
    }

    return {
      type,
      childRefs: childRefsCopy,
      metadata: metadataCopy
    };
  }

  function getSectionAttributes() {
    const { attributes } = cloneDeep(auxRoot.current) as SectionContainer;

    return attributes;
  }

  function setSectionAttributes(newAttributes: SectionAttributes) {
    setNewRoot({
      ...(auxRoot.current as SectionContainer),
      attributes: newAttributes
    });
  }

  function deleteResolvedFieldsFromExternalDTO<T extends Container>(dto: {
    root: T;
    metadata: BaseMetadata;
  }): {
    root: T;
    metadata: BaseMetadata;
  } {
    const dtoCopy = cloneDeep(dto);
    traverseEntity(dtoCopy.root, (entity) => {
      if (entity.type === EntityType.Article) {
        const articleItem = entity as ArticleItem;
        delete articleItem.content;
      }
      if (entity.type === EntityType.Collection) {
        const collectionItem = entity as ExternalCollectionItem;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete collectionItem.contentItems;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete collectionItem.metadata;
      }
      if (entity.type === EntityType.Query) {
        const queryItem = entity as QueryItem;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete queryItem.queryItems;
      }
    });
    return dtoCopy;
  }

  const fromAllessehContent = useCallback(
    (
      content: AllessehContent | AllessehSummariesIncludedAttributes,
      options?: FromAllessehContentOptions
    ): ArticleItem | LinkedItem => {
      const {
        id: originId,
        attributes: {
          product,
          seo_id: seoId,
          canonical_url: canonicalUrl,
          source_url: sourceUrl,
          content_status: status,
          embargo_datetime_utc: embargoDatetimeUtc
        }
      } = content.data;
      let originContentType = content.data.type;

      // Handle LinkedItem type
      if (originContentType.toLowerCase() === 'link') {
        const attributes = content.data.attributes as {
          headline?: { text?: string };
          summary?: { content?: Array<{ content?: Array<{ text?: string }> }> };
        };
        const linkedItem: LinkedItem = {
          type: EntityType.LinkedItem,
          uuid: options?.generateUUID ? uuidv4() : '',
          attributes: {
            type: 'link',
            id: canonicalUrl || sourceUrl,
            hosted_url: canonicalUrl || sourceUrl,
            product: product || '',
            title: attributes.headline?.text ?? '',
            description: attributes.summary?.content?.[0]?.content?.[0]?.text ?? ''
          }
        };
        return linkedItem;
      }

      if (!isArticleOrMediaContentType(originContentType)) {
        if (originContentType === 'SummaryModel') {
          originContentType = content.data.attributes.type;
        } else {
          // fall back to article if unrecognized content type is sent
          originContentType = 'article';
          reportError(`Unknown origin content type: ${content.data.type} for ${originId}, falling back to article`);
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const normalizedSeoId = seoId ?? '';

      const articleItem: ArticleItem = {
        type: EntityType.Article,
        uuid: options?.generateUUID ? uuidv4() : '',
        content: JSON.stringify(content),
        attributes: {
          base_doc_id: originId,
          hosted_url: canonicalUrl || sourceUrl,
          id: originId,
          link: originContentType === 'commerceproduct' ? '' : `api/articles/v1/originid/${originId}`,
          originContentType,
          originId,
          product,
          seo_id: originContentType === 'commerceproduct' ? '' : normalizedSeoId,
          status,
          embargoDatetimeUtc
        }
      };

      return articleItem;
    },
    []
  );

  function relocateMultizoneModuleItems(
    srcHierarchyIdSanitized: string,
    destHierarchyIdSanitized: string,
    srcEntity:
      | MultiListContainer
      | SectionContainer
      | PageContainer
      | BannerModuleContainer
      | ModuleContainer
      | MultiZoneModuleContainer
      | ListContainer
      | OffPlatformListContainer
      | NewswiresListContainer
      | NewsletterPageContainer
      | IssueContainer
      | IssueSectionContainer,

    destEntity:
      | MultiListContainer
      | SectionContainer
      | PageContainer
      | BannerModuleContainer
      | ModuleContainer
      | MultiZoneModuleContainer
      | ListContainer
      | OffPlatformListContainer
      | NewswiresListContainer
      | NewsletterPageContainer
      | IssueContainer
      | IssueSectionContainer,
    rootCopy: Container
  ) {
    const zoneValues = ['L', 'M', 'S'];

    const multizoneSrcHierarchySanitized = replaceZoneIdentifiers(srcHierarchyIdSanitized);
    const multizoneDestHierarchySanitized = replaceZoneIdentifiers(destHierarchyIdSanitized);

    const multizoneSrcIndices = readHierarchyId(multizoneSrcHierarchySanitized, true);
    const multizoneDestIndices = readHierarchyId(multizoneDestHierarchySanitized, true);

    const [, , srcModuleIndex, srcZoneIndex, srcItemIndex] = multizoneSrcIndices;
    const [, , destModuleIndex, destZoneIndex, destItemIndex] = multizoneDestIndices;

    if (srcModuleIndex === destModuleIndex) {
      const module = srcEntity as MultiZoneModuleContainer;

      const itemIndex = module.attributes.zones![srcZoneIndex].contentIndexes![srcItemIndex];
      const item = module.collection[itemIndex] as MobileArticleItem;

      if (typeof zoneValues.at(destZoneIndex) === 'string') {
        const entityToMove = module.collection[itemIndex];
        // Handle mobile linked item. If we add another content type to multizone modules we need
        // to also add support here
        if (isMobileLinkedItem(entityToMove) || isGraphicLinkedItem(entityToMove)) {
          const destZoneName = zoneValues.at(destZoneIndex);
          if (destZoneName) {
            entityToMove.attributes.size = destZoneName;
          }
          module.collection[itemIndex] = entityToMove;
        } else {
          try {
            const newItem = makeMobileArticle({
              article: item,
              rulesConfig: data?.publicationSetting.rulesConfig,
              zone: destZoneIndex
            });

            module.collection[itemIndex] = newItem;
          } catch (error) {
            if (error instanceof Error) {
              alertError(error.message);
            } else {
              alertError('An unexpected error occurred while relocating the item. Please try again.');
            }

            return;
          }
        }
      }

      const [removed] = module.attributes.zones![srcZoneIndex].contentIndexes!.splice(srcItemIndex, 1);
      module.attributes.zones![destZoneIndex].contentIndexes!.splice(destItemIndex, 0, removed);

      setNewRoot(rootCopy);

      return;
    }

    const srcModule = srcEntity as MultiZoneModuleContainer;

    const itemIndexToRemove = srcModule.attributes.zones![srcZoneIndex].contentIndexes![srcItemIndex];
    const itemToRemove = srcModule.collection[itemIndexToRemove] as
      MobileArticleItem | MobileLinkedItem | GraphicLinkedItem;

    const destModule = destEntity as MultiZoneModuleContainer;

    srcModule.attributes.zones![srcZoneIndex].contentIndexes!.splice(srcItemIndex, 1);

    srcModule.collection.splice(itemIndexToRemove, 1);
    adjustContentIndexes(srcModule.attributes.zones!, itemIndexToRemove);

    // Additional check for mobile linked items and graphic linked items as they will fail on `makeMobileArticle`
    if (isMobileLinkedItem(itemToRemove) || isGraphicLinkedItem(itemToRemove)) {
      const destZoneName = zoneValues.at(destZoneIndex);
      if (destZoneName) {
        itemToRemove.attributes.size = destZoneName;
      }
      destModule.collection.push(itemToRemove);
    } else if (typeof zoneValues.at(destZoneIndex) === 'string') {
      try {
        const newItem = makeMobileArticle({
          article: itemToRemove,
          rulesConfig: data?.publicationSetting.rulesConfig,
          zone: destZoneIndex
        });

        destModule.collection.push(newItem);
      } catch (error) {
        if (error instanceof Error) {
          alertError(error.message);
        } else {
          alertError('An unexpected error occurred while relocating the item. Please try again.');
        }

        return;
      }
    }

    const indexOfLastArticle = destModule.collection.length - 1;

    destModule.attributes.zones![destZoneIndex].contentIndexes!.splice(destItemIndex, 0, indexOfLastArticle);

    setNewRoot(rootCopy);
  }

  function relocateMobileArticle(
    srcHierarchyIdSanitized: string,
    srcEntity:
      | MultiListContainer
      | SectionContainer
      | PageContainer
      | BannerModuleContainer
      | ModuleContainer
      | MultiZoneModuleContainer
      | ListContainer
      | OffPlatformListContainer
      | NewswiresListContainer
      | NewsletterPageContainer
      | IssueContainer
      | IssueSectionContainer,

    destEntity:
      | MultiListContainer
      | SectionContainer
      | PageContainer
      | BannerModuleContainer
      | ModuleContainer
      | MultiZoneModuleContainer
      | ListContainer
      | OffPlatformListContainer
      | NewswiresListContainer
      | NewsletterPageContainer
      | IssueContainer
      | IssueSectionContainer,
    rootCopy: Container
  ) {
    const multizoneSrcHierarchySanitized = replaceZoneIdentifiers(srcHierarchyIdSanitized);

    const multizoneSrcIndices = readHierarchyId(multizoneSrcHierarchySanitized, true);

    const [, , , srcZoneIndex, srcItemIndex] = multizoneSrcIndices;

    const srcModule = srcEntity as MultiZoneModuleContainer;

    const itemIndexToRemove = srcModule.attributes.zones![srcZoneIndex].contentIndexes![srcItemIndex];
    const itemToRemove = srcModule.collection[itemIndexToRemove] as MobileArticleItem | GraphicLinkedItem;

    srcModule.collection.splice(itemIndexToRemove, 1); // Remove the item from the source module

    adjustContentIndexes(srcModule.attributes.zones!, itemIndexToRemove);

    const destModule = destEntity as ModuleContainer;

    if (isGraphicLinkedItem(itemToRemove)) {
      destModule.collection.push(itemToRemove);
    } else {
      const newItem = {
        ...itemToRemove,
        subtype: null,
        attributes: {
          ...itemToRemove.attributes,
          size: null,
          treatment: null,
          showFlashline: null,
          visual: null
        }
      };

      destModule.collection.push(newItem as ArticleItem);
    }

    setNewRoot(rootCopy);
  }

  function swapEntities(srcHierarchyId: string, destHierarchyId: string) {
    const rootCopy = cloneDeep(auxRoot.current);

    const sanitize = (id: string) => id.replace(/.+?#/, '');
    const getIndices = (idSanitized: string) => readHierarchyId(idSanitized, true);

    const srcHierarchyIdSanitized = sanitize(srcHierarchyId);
    const destHierarchyIdSanitized = sanitize(destHierarchyId);

    const srcIndices = getIndices(srcHierarchyIdSanitized);
    const destIndices = getIndices(destHierarchyIdSanitized);

    const lastSrcIndex = srcIndices.pop()!;
    const lastDestIndex = destIndices.pop()!;

    const srcEntity = getEntity(srcHierarchyId, { indices: srcIndices, root: rootCopy });
    const destEntity = getEntity(destHierarchyId, { indices: destIndices, root: rootCopy });

    const entityBeingMoved = srcEntity?.collection[lastSrcIndex];

    const isSrcEntityMobileLinkedItem = entityBeingMoved && isMobileLinkedItem(entityBeingMoved);

    if (
      srcEntity?.type === EntityType.Module &&
      destEntity?.type === EntityType.Module &&
      srcEntity.subtype === MULTIZONE_MODULE &&
      destEntity.subtype === MULTIZONE_MODULE
    ) {
      relocateMultizoneModuleItems(srcHierarchyIdSanitized, destHierarchyIdSanitized, srcEntity, destEntity, rootCopy);

      return;
    }

    // Prevent mobile linked items from being added to non-multizone modules
    if (
      srcEntity?.type === EntityType.Module &&
      destEntity?.type === EntityType.Module &&
      isSrcEntityMobileLinkedItem
    ) {
      alertError('You cannot add mobile linked items to non-multizone modules.');

      return;
    }

    // From multizone module to non-multizone module
    if (
      srcEntity?.type === EntityType.Module &&
      destEntity?.type === EntityType.Module &&
      srcEntity.subtype === MULTIZONE_MODULE &&
      destEntity.subtype !== MULTIZONE_MODULE
    ) {
      relocateMobileArticle(srcHierarchyIdSanitized, srcEntity, destEntity, rootCopy);

      return;
    }

    // From non-multizone module to multizone module
    if (
      srcEntity?.type === EntityType.Module &&
      destEntity?.type === EntityType.Module &&
      srcEntity.subtype !== MULTIZONE_MODULE &&
      destEntity.subtype === MULTIZONE_MODULE
    ) {
      const [removed] = srcEntity.collection.splice(lastSrcIndex, 1);
      addEntityToMultiZoneModule(destHierarchyIdSanitized, destEntity, removed, rootCopy);

      return;
    }

    // @ts-expect-error - one of these entities is a container, the other may be null
    const [removed] = srcEntity.collection.splice(lastSrcIndex, 1);
    // @ts-expect-error - splice expect a specific entity type, not a union of entity types
    destEntity.collection.splice(lastDestIndex, 0, removed);

    setNewRoot(rootCopy);
  }

  function isContainer(item: EntityComponent): item is Container {
    return (
      item.type === 'SectionContainer' ||
      item.type === 'PageContainer' ||
      item.type === 'ModuleContainerUnion' ||
      item.type === 'ListContainer' ||
      item.type === 'OffPlatformListContainer'
    );
  }

  const existsItemInCollection = (item: ContentItem, collection: Container[]): boolean =>
    collection.some((existItem) => {
      if (isContainer(existItem)) {
        return existsItemInCollection(item, existItem.collection as Container[]);
      }

      return compareItems(item, existItem);
    });

  function isItemInDataModel(item: ContentItem) {
    return existsItemInCollection(item, root.collection as Container[]);
  }

  function insertEntityToCollection(destHierarchyId: string, entity: ModuleContainerUnion) {
    const rootCopy = cloneDeep(root);

    if (!('collection' in rootCopy)) {
      throw new Error('Destination entity is not a container');
    }

    if (rootCopy.collection.length === 0) {
      rootCopy.collection = [entity];
    } else {
      // eslint-disable-next-line no-lonely-if
      if ('collection' in rootCopy.collection[0]) {
        (rootCopy.collection[0].collection as ContentUnion[]).push(...entity.collection);
      }
    }

    setNewRoot(rootCopy);
  }

  function addEntityToMultiZoneModule(
    destHierarchyIdSanitized: string,
    destEntity: Container,
    entity: EntityComponent,
    rootCopy: Container
  ) {
    const multizoneHierarchy = replaceZoneIdentifiers(destHierarchyIdSanitized);
    const multizoneIndexes = readHierarchyId(multizoneHierarchy, true);

    const module = destEntity as MultiZoneModuleContainer;

    let zone = multizoneIndexes.at(-2)!;

    const rulesConfig = data?.publicationSetting.rulesConfig;

    let contentIndexes = [];

    let indexInZone: number;
    const isPlainLinkedItem =
      entity.type === EntityType.LinkedItem && !isMobileLinkedItem(entity) && !isGraphicLinkedItem(entity);
    // Additional check for mobile linked items and graphic linked items as they will fail on `makeMobileArticle`
    if (isMobileLinkedItem(entity) || isGraphicLinkedItem(entity) || isPlainLinkedItem) {
      const zones = module.attributes.zones ?? [];

      if (isMobileLinkedItem(entity) || isPlainLinkedItem) {
        // MobileLinkedItem and plain LinkedItem always land in the default zone
        const defaultZone =
          data?.publicationSetting.defaultLinkedItemType === LinkedItemCardType.Graphics
            ? DEFAULT_GRAPHIC_LINKED_ITEM_ZONE
            : DEFAULT_LINKED_ITEM_ZONE;
        zone = zones.findIndex((z) => z.name === defaultZone);
      }
      // GraphicLinkedItem uses the actual drop zone (zone already set from multizoneIndexes.at(-2))

      const zoneName = zones[zone]?.name;
      if (zoneName) {
        Object.assign(entity.attributes, { size: zoneName });
      }
      module.collection.push(entity as unknown as ContentUnion);
      const indexOfNewEntity = module.collection.length - 1;

      const sZone = zones[zone];
      const sIndexes = (sZone.contentIndexes ? [...sZone.contentIndexes] : []) as number[];
      const insertAt = sIndexes.length;
      indexInZone = insertAt;
      contentIndexes = sIndexes.toSpliced(insertAt, 0, indexOfNewEntity);
    } else {
      try {
        const mobileArticle = makeMobileArticle({
          article: entity as MobileArticleItem,
          zone,
          rulesConfig
        });
        module.collection.push(mobileArticle);
        const itemIndex = multizoneIndexes.at(-1)!;
        indexInZone = itemIndex;
        const indexOfLastArticle = module.collection.indexOf(mobileArticle);
        contentIndexes = module.attributes.zones![zone].contentIndexes!.toSpliced(itemIndex, 0, indexOfLastArticle);
      } catch (error) {
        if (error instanceof Error) {
          alertError(error.message);
        } else {
          alertError('An unexpected error occurred while adding the item. Please try again.');
        }

        return false;
      }
    }

    module.attributes.zones![zone].contentIndexes = contentIndexes;
    setNewRoot(rootCopy);

    return `${multizoneHierarchy.replace(/-\d+$/, '')}_zone-${zone}_${entity.type.toLowerCase()}-${indexInZone!}`;
  }

  function insertEntity(destHierarchyId: string, entity: EntityComponent | EntityComponent[]) {
    const rootCopy = cloneDeep(auxRoot.current);

    const destHierarchyIdSanitized = destHierarchyId.replace(/.+?#/, '');
    const destHierarchyIdIndices = readHierarchyId(destHierarchyIdSanitized, true);
    const lastDestIndex = destHierarchyIdIndices.pop();

    const destEntity = getEntity(destHierarchyId, { indices: destHierarchyIdIndices, root: rootCopy });

    if (!destEntity || !('collection' in destEntity)) {
      throw new Error('Destination entity is not a container');
    }

    if (Array.isArray(entity)) {
      // @ts-expect-error - splice expect a specific entity type, not a union of entity types
      destEntity.collection.splice(lastDestIndex, 0, ...entity);
    } else {
      if (destEntity.subtype === MULTIZONE_MODULE) {
        const didAdd = addEntityToMultiZoneModule(destHierarchyIdSanitized, destEntity, entity, rootCopy);

        return didAdd;
      }

      // @ts-expect-error - splice expect a specific entity type, not a union of entity types
      destEntity.collection.splice(lastDestIndex, 0, entity);
    }

    setNewRoot(rootCopy);

    return true;
  }

  function modifyEntity(hierarchyId: string, callback: (entity: EntityComponent) => void) {
    const rootCopy = cloneDeep(auxRoot.current);

    const hierarchyIdSanitized = hierarchyId.replace(/.+?#/, '');
    const hierarchyIdIndices = readHierarchyId(hierarchyIdSanitized, true);

    // "section-0_page-0_module-0_zone-0_article-2" a multizone hierarchy id
    if (hierarchyIdSanitized.includes('zone')) {
      const itemIndex = hierarchyIdIndices.pop();
      const zone = hierarchyIdIndices.pop();

      const multizoneModule = getEntity(hierarchyId, {
        indices: hierarchyIdIndices,
        root: rootCopy
      }) as MultiZoneModuleContainer;

      const collectionIndex = multizoneModule.attributes.zones![zone!].contentIndexes![itemIndex!];

      const entity = multizoneModule.collection[collectionIndex];

      callback(entity);

      setNewRoot(rootCopy);

      return;
    }

    let destEntity: EntityComponent | null = null;
    // eslint-disable-next-line no-restricted-syntax
    for (const index of hierarchyIdIndices) {
      if (!destEntity) {
        destEntity = rootCopy;
      } else {
        destEntity = destEntity.collection[index];
      }
    }

    if (destEntity) {
      callback(destEntity);
    }

    setNewRoot(rootCopy);
  }

  function clearModule(hierarchyId: string) {
    const rootCopy = cloneDeep(auxRoot.current);

    const hierarchyIdSanitized = hierarchyId.replace(/.+?#/, '');
    const hierarchyIdIndices = readHierarchyId(hierarchyIdSanitized, true);

    // "section-0_page-0_module-0_zone-0_article-2" a multizone hierarchy id
    if (hierarchyIdSanitized.includes('zone')) {
      const itemIndex = hierarchyIdIndices.pop();
      const zone = hierarchyIdIndices.pop();

      const multizoneModule = getEntity(hierarchyId, {
        indices: hierarchyIdIndices,
        root: rootCopy
      }) as MultiZoneModuleContainer;

      const collectionIndex = multizoneModule.attributes.zones![zone!].contentIndexes![itemIndex!];
      const entity = multizoneModule.collection[collectionIndex];

      if ('collection' in entity) {
        entity.collection = [];
      }

      setNewRoot(rootCopy);

      return;
    }

    // Use getEntity to find the target entity within the cloned tree
    const entity = getEntity(hierarchyId, { indices: hierarchyIdIndices, root: rootCopy });

    if (entity && 'collection' in entity) {
      entity.collection = [];
    }

    setNewRoot(rootCopy);
  }

  function modifyAllByType<E extends EntityComponent = EntityComponent>(type: string, callback: (entity: E) => void) {
    const entityHierarchyIds: string[] = [];

    traverseEntity(auxRoot.current, (entity, hierarchyId) => {
      if (entity.type === type) {
        entityHierarchyIds.push(hierarchyId);
      }
    });

    entityHierarchyIds.forEach((hierarchyId) =>
      modifyEntity(hierarchyId, (entity) => {
        if (entity.type === type) {
          callback(entity as E);
        }
      })
    );
  }

  function applyOverfetchForAddedQuery(
    moduleHierarchyId: string,
    query: Record<string, unknown>,
    queryIdToExclude?: string
  ): PageModule | null {
    let updatedPageModule: PageModule | null = null;

    // Step 1: Find all modules with matching queries
    const modulesWithMatchingQuery: string[] = [];

    traverseEntity(auxRoot.current, (entity, hierarchyId) => {
      if (entity.type === EntityType.Module) {
        const moduleContainer = entity as ModuleContainer;
        if (
          moduleContainer.attributes.pageModule &&
          moduleContainsMatchingQuery(moduleContainer, query, queryIdToExclude)
        ) {
          modulesWithMatchingQuery.push(hierarchyId);
        }
      }
    });

    // Step 2: Calculate the overfetch value based on total occurrences
    const totalOccurrences = modulesWithMatchingQuery.length + 1;
    const overfetchBy = calculateOverfetchValue(totalOccurrences);

    // Step 3: Update the target module (the one being added/modified)
    modifyEntity(moduleHierarchyId, (entity) => {
      if (entity.type === EntityType.Module) {
        const moduleContainer = entity as ModuleContainer;
        if (moduleContainer.attributes.pageModule) {
          if (!moduleContainer.attributes.pageModule.uiModuleType) return;
          const moduleFieldKey = getModuleFieldKey(moduleContainer.attributes.pageModule.uiModuleType);
          const moduleFields = moduleContainer.attributes.pageModule.uiModuleFields?.[moduleFieldKey];

          if (moduleFields && 'overfetchBy' in moduleFields) {
            (moduleFields as { overfetchBy?: number | null }).overfetchBy = overfetchBy;
          }

          updatedPageModule = moduleContainer.attributes.pageModule;
        }
      }
    });

    // Step 4: Update all other modules with matching queries
    modulesWithMatchingQuery.forEach((hierarchyId) => {
      modifyEntity(hierarchyId, (entity) => {
        if (entity.type === EntityType.Module) {
          const moduleContainer = entity as ModuleContainer;
          if (moduleContainer.attributes.pageModule) {
            if (!moduleContainer.attributes.pageModule.uiModuleType) return;
            const moduleFieldKey = getModuleFieldKey(moduleContainer.attributes.pageModule.uiModuleType);
            const moduleFields = moduleContainer.attributes.pageModule.uiModuleFields?.[moduleFieldKey];

            if (moduleFields && 'overfetchBy' in moduleFields) {
              (moduleFields as { overfetchBy?: number | null }).overfetchBy = overfetchBy;
            }
          }
        }
      });
    });

    return updatedPageModule;
  }

  function applyOverfetchForModifiedQuery(queryId: string, query: Record<string, unknown>) {
    // Find the parent module that contains this query
    let moduleHierarchyId = '';

    traverseEntity(auxRoot.current, (entity, currentHierarchyId) => {
      if (entity.type === EntityType.Module) {
        const moduleContainer = entity as ModuleContainer;
        const hasQuery = moduleContainer.collection.some(
          (item) => item.type === EntityType.Query && (item as QueryItem).attributes.id === queryId
        );
        if (hasQuery && moduleContainer.attributes.pageModule) {
          moduleHierarchyId = currentHierarchyId;
        }
      }
    });

    // Apply overfetch if module was found
    if (moduleHierarchyId) {
      applyOverfetchForAddedQuery(moduleHierarchyId, query, queryId);
    }
  }

  function removeEntityFromMultizoneModule(
    hierarchyId: string,
    destEntity:
      | MultiListContainer
      | SectionContainer
      | PageContainer
      | BannerModuleContainer
      | ModuleContainer
      | MultiZoneModuleContainer
      | ListContainer
      | OffPlatformListContainer
      | NewswiresListContainer
      | NewsletterPageContainer
      | IssueContainer
      | IssueSectionContainer,
    rootCopy: Container
  ) {
    const multizoneHierarchyId = replaceZoneIdentifiers(hierarchyId);
    const multizoneIndices = readHierarchyId(multizoneHierarchyId, true);

    const [, , , zoneIndex, itemIndex] = multizoneIndices;

    const module = destEntity as MultiZoneModuleContainer;

    const itemIndexToRemove = module.attributes.zones![zoneIndex].contentIndexes![itemIndex];

    const [removedItem] = module.collection.splice(itemIndexToRemove, 1); // Remove the item from the source module

    adjustContentIndexes(module.attributes.zones!, itemIndexToRemove);

    setNewRoot(rootCopy);

    return removedItem;
  }

  function removeEntity(hierarchyId: string, cleanupDedupe = true) {
    const rootCopy = cloneDeep(auxRoot.current);

    let hierarchyIdSanitized = hierarchyId.replace(/.+?#/, '');
    let hierarchyIdIndices = readHierarchyId(hierarchyIdSanitized, true);

    if (hierarchyIdSanitized.includes('zone')) {
      hierarchyIdSanitized = replaceZoneIdentifiers(hierarchyIdSanitized);
      hierarchyIdIndices = readHierarchyId(hierarchyIdSanitized, true);
      hierarchyIdIndices.pop();
    }

    const lastDestIndex = hierarchyIdIndices.pop()!;

    const destEntity = hierarchyIdIndices.length
      ? getEntity(hierarchyId, { indices: hierarchyIdIndices, root: rootCopy })
      : (rootCopy as EntityComponent);
    if (!destEntity || !('collection' in destEntity)) {
      throw new Error('Destination entity is not a container');
    }

    const itemToRemove = destEntity.collection[lastDestIndex];

    if (cleanupDedupe && (itemToRemove as EntityComponent | undefined) != null) {
      if (isArticleItem(itemToRemove)) {
        removeDedupedNotRulesFromTree(rootCopy, itemToRemove.attributes.id, traverseEntity);
      } else if ('collection' in itemToRemove) {
        traverseEntity(itemToRemove, (child) => {
          if (isArticleItem(child)) {
            removeDedupedNotRulesFromTree(rootCopy, child.attributes.id, traverseEntity);
          }
        });
      }
    }

    if (destEntity.type === EntityType.Module && destEntity.subtype === MULTIZONE_MODULE) {
      removeEntityFromMultizoneModule(hierarchyIdSanitized, destEntity, rootCopy);

      return null;
    }

    const [removedItem] = destEntity.collection.splice(lastDestIndex, 1);

    setNewRoot(rootCopy);
    setHasModelChangedOnce(true);

    return removedItem;
  }

  function removeAllByType(type: string) {
    const entityHierarchyIds: string[] = [];

    traverseEntity(auxRoot.current, (entity, hierarchyId) => {
      if (entity.type === type) {
        entityHierarchyIds.push(hierarchyId);
      }
    });

    entityHierarchyIds.toReversed().forEach((hierarchyId) => removeEntity(hierarchyId));
  }

  function cleanupDedupeForGroup(moduleHierarchyIds: string[]) {
    const rootCopy = cloneDeep(auxRoot.current);

    moduleHierarchyIds.forEach((moduleHierarchyId) => {
      const module = getEntity(moduleHierarchyId, { root: rootCopy });

      if (module && 'collection' in module) {
        (module as ModuleContainer).collection.forEach((item) => {
          if (isArticleItem(item)) {
            removeDedupedNotRulesFromTree(rootCopy, item.attributes.id, traverseEntity);
          }
        });
      }
    });

    setNewRoot(rootCopy);
  }

  function changeEntity(hierarchyId: string, callback: (oldEntity: EntityComponent) => EntityComponent) {
    const rootCopy = cloneDeep(auxRoot.current);

    const hierarchyIdSanitized = hierarchyId.replace(/.+?#/, '');
    const hierarchyIdIndices = readHierarchyId(hierarchyIdSanitized, true);

    let destEntity: EntityComponent | null = null;
    // eslint-disable-next-line no-restricted-syntax
    for (const index of hierarchyIdIndices) {
      if (!destEntity) {
        destEntity = rootCopy;
      } else {
        destEntity = destEntity.collection[index];
      }
    }

    if (destEntity) {
      const newEntity = callback(destEntity);

      removeEntity(hierarchyId, false);
      insertEntity(hierarchyId, newEntity);
    }
  }

  function getEmbargoContent() {
    const embargoContent: ArticleItem[] = [];

    traverseEntity(auxRoot.current, (entity) => {
      if (entity.type === EntityType.Article) {
        const articleItem = entity as ArticleItem;
        if (articleItem.attributes.status === 'embargo' && !articleItem.attributes.isCancelledEmbargo) {
          embargoContent.push(articleItem);
        }
      }
    });

    return embargoContent;
  }

  function getCancelledEmbargoContent() {
    const cancelledEmbargoContent: ArticleItem[] = [];

    traverseEntity(auxRoot.current, (entity) => {
      if (entity.type === EntityType.Article) {
        const articleItem = entity as ArticleItem;
        if (articleItem.attributes.isCancelledEmbargo) {
          cancelledEmbargoContent.push(articleItem);
        }
      }
    });

    return cancelledEmbargoContent;
  }

  function embargoSortFunction(a: AllessehContent, b: AllessehContent) {
    const aDateTime = a.data.attributes.embargo_datetime_utc;
    const bDateTime = b.data.attributes.embargo_datetime_utc;

    const aDate = new Date(aDateTime!);
    const bDate = new Date(bDateTime!);

    return aDate.getTime() - bDate.getTime();
  }

  function getSortedEmbargoContent(sort: 'asc' | 'desc'): AllessehContent[] {
    const embargoContent = getEmbargoContent().map((item) => safelyParseContent<AllessehContent>(item));

    if (sort === 'desc') {
      return embargoContent.sort((a, b) => embargoSortFunction(b, a));
    }

    return embargoContent.sort(embargoSortFunction);
  }

  /**
   * Dedupes the queries in the data model by adding all article IDs to the `not` rules of each query.
   * The modification is done in-place on the root container.
   */
  function dedupe(root: Container) {
    const allArticleIds = getAllArticleIds();

    traverseEntity(root, (entity) => {
      if (entity.type === EntityType.Query) {
        const query = entity as QueryItem;
        const queryBody = query.attributes.query as AllessehContentQueryBody;
        const notRules: AllessehQueryRule[] = queryBody.query?.not ?? [];

        allArticleIds.forEach((articleId) => {
          const isArticleAlreadyExcluded = notRules.some(
            (rule) => 'term' in rule && rule.term.key === 'UpstreamOriginId' && rule.term.value === articleId
          );

          if (isArticleAlreadyExcluded) {
            return;
          }

          notRules.push({
            term: {
              key: 'UpstreamOriginId',
              value: articleId
            },
            meta: { deduped: true }
          });
        });

        if (!queryBody.query) {
          queryBody.query = {};
        }
        queryBody.query.not = notRules;
      }
    });
  }

  function renderEntity(entity: EntityComponent, options: RenderEntityOptions): React.ReactElement {
    const {
      hierarchyId,
      index = 0,
      isHistory = false,
      withoutDragNDrop,
      extraProps,
      isInEditMode,
      isReadOnly
    } = options;

    let entityComponent: React.ReactNode | null = null;

    if ('collection' in entity && entity.type !== EntityType.Group) {
      const groups = groupBy(entity.collection, (node) =>
        'groupId' in node && typeof node.groupId === 'string' ? node.groupId : 'ungrouped'
      );
      // TODO: handle case when there are groups and ungrouped items at the same level
      delete groups.ungrouped;

      const groupsArr = Object.entries(groups);
      if (groupsArr.length > 0) {
        const structure = structureRef.current;
        // eslint-disable-next-line no-param-reassign
        entity.collection = groupsArr.map(([groupId, group]) => {
          // Old-shape pages store isGroupEnabled: true directly on each module.
          // New structure pages don't — the group's enabled/disabled state comes from structure's
          // defaultValue. Check for an explicit flag first (old shape), then fall back to defaultValue
          // (new shape). Without the fallback, structure groups would always render disabled on first load.
          const hasExplicitGroupEnabled = group.some(
            (item) => 'isGroupEnabled' in item && item.isGroupEnabled === true
          );
          const configDefaultValue = structure?.groups.find((g) => g.id === groupId)?.config.defaultValue;
          const isGroupEnabled = hasExplicitGroupEnabled || (configDefaultValue ?? false);
          return {
            __typename: 'GroupContainer',
            type: 'Group',
            collection: group,
            // groupId of this group, does not mean that group is part of another group
            groupId,
            isGroupEnabled
          } as GroupContainer;
        });
      }
    }

    if (entity.type === EntityType.Group) {
      const group = entity as GroupContainer;
      entityComponent = (
        <GroupProvider group={group}>
          <Group
            data={group}
            parentHierarchyId={hierarchyId}
            index={index}
            isHistory={isHistory}
            extraProps={extraProps}
            isReadOnly={isReadOnly}
          />
        </GroupProvider>
      );
    }

    if (entity.type === EntityType.MultiList) {
      const multiList = entity as MultiListContainer;
      entityComponent = <MultiList data={multiList} parentHierarchyId={hierarchyId} index={index} />;
    }

    if (entity.type === EntityType.Issue) {
      const issue = entity as IssueContainer;
      entityComponent = <Issue data={issue} parentHierarchyId={hierarchyId} index={index} />;
    }

    if (entity.type === EntityType.Section) {
      const section = entity as SectionContainer;
      entityComponent = (
        <Section
          data={section}
          parentHierarchyId={hierarchyId}
          index={index}
          isHistory={isHistory}
          isReadOnly={isReadOnly}
        />
      );

      if (isIssueSectionContainer(entity)) {
        entityComponent = (
          <IssueSection data={entity} parentHierarchyId={hierarchyId} index={index} isHistory={isHistory} />
        );
      }
    }

    if (entity.type === EntityType.Page) {
      const page = entity as PageContainer;
      entityComponent = (
        <Page data={page} parentHierarchyId={hierarchyId} index={index} isHistory={isHistory} isReadOnly={isReadOnly} />
      );

      if ('subtype' in entity && entity.subtype === 'NewsletterPage') {
        const newsletter = entity as NewsletterPageContainer;
        entityComponent = (
          <Newsletter
            data={newsletter}
            parentHierarchyId={hierarchyId}
            index={index}
            withoutDragNDrop={withoutDragNDrop}
          />
        );
      }
    }

    if (entity.type === EntityType.Module) {
      const module = entity as ModuleContainer;

      if ('subtype' in entity && entity.subtype === 'BannerModule') {
        const bannerModule = entity as BannerModuleContainer;
        entityComponent = (
          <BannerModule
            data={bannerModule}
            parentHierarchyId={hierarchyId}
            index={index}
            isHistory={isHistory}
            isReadOnly={isReadOnly}
          />
        );
      } else {
        entityComponent = (
          <Module
            data={module}
            parentHierarchyId={hierarchyId}
            index={index}
            isHistory={isHistory}
            groupId={module.groupId}
            withoutDragNDrop={withoutDragNDrop}
            isReadOnly={isReadOnly}
          />
        );
      }

      if ('subtype' in entity && entity.subtype === MULTIZONE_MODULE) {
        const multiZoneModule = entity as MultiZoneModuleContainer;
        entityComponent = (
          <MultiZoneModule
            data={multiZoneModule}
            parentHierarchyId={hierarchyId}
            index={index}
            isHistory={isHistory}
            groupId={module.groupId}
            isReadOnly={isReadOnly}
          />
        );
      }
    }

    if (entity.type === EntityType.List) {
      const list = entity as ListContainer;
      entityComponent = (
        <List data={list} parentHierarchyId={hierarchyId} index={index} isHistory={isHistory} isReadOnly={isReadOnly} />
      );

      if ('subtype' in entity && entity.subtype === 'OffPlatformList') {
        const offPlatform = entity as OffPlatformListContainer;
        entityComponent = (
          <OffPlatform
            data={offPlatform}
            parentHierarchyId={hierarchyId}
            index={index}
            isHistory={isHistory}
            isInEditMode={isInEditMode}
          />
        );
      } else if ('subtype' in entity && entity.subtype === 'NewswiresList') {
        const newswiresList = entity as NewswiresListContainer;
        entityComponent = (
          <NewswiresList data={newswiresList} parentHierarchyId={hierarchyId} index={index} isHistory={isHistory} />
        );
      }
    }

    if (entity.type === EntityType.Article) {
      const article = entity as ArticleItem;

      const rulesConfig = data?.publicationSetting.rulesConfig;

      entityComponent = (
        <Article
          data={article}
          parentHierarchyId={hierarchyId}
          index={index}
          isHistory={isHistory}
          rulesConfig={rulesConfig}
          {...options.extraProps?.content}
          {...options.extraProps?.article}
        />
      );
    }

    if (entity.type === EntityType.Collection) {
      const collection = entity as ExternalCollectionItem;
      entityComponent = (
        <Collection
          data={collection}
          parentHierarchyId={hierarchyId}
          index={index}
          isHistory={isHistory}
          {...options.extraProps?.content}
        />
      );
    }

    if (entity.type === EntityType.Query) {
      const query = entity as QueryItem;
      if (query.attributes.isFeed) {
        const fullHierarchyId = generateHierarchyId(query, hierarchyId, index);

        entityComponent = (
          <Feed
            data={query}
            isHistory={isHistory}
            hierarchyId={fullHierarchyId}
            {...options.extraProps?.module}
            {...options.extraProps?.content}
            isReadOnly={isReadOnly}
          />
        );
      } else {
        entityComponent = (
          <Query
            data={query}
            parentHierarchyId={hierarchyId}
            index={index}
            isHistory={isHistory}
            {...options.extraProps?.content}
          />
        );
      }
    }

    if (isGraphicLinkedItem(entity)) {
      entityComponent = (
        <LinkedItemComponent
          data={entity}
          parentHierarchyId={hierarchyId}
          index={index}
          isHistory={isHistory}
          isReadOnly={isReadOnly}
          {...options.extraProps?.content}
        />
      );
    } else if (isMobileLinkedItem(entity)) {
      const linkedItem = entity;
      entityComponent = (
        <LinkedItemComponent
          data={linkedItem}
          parentHierarchyId={hierarchyId}
          index={index}
          isHistory={isHistory}
          isReadOnly={isReadOnly}
          {...options.extraProps?.content}
        />
      );
    } else if (entity.type === EntityType.LinkedItem) {
      const linkedItem = entity as LinkedItem;
      entityComponent = (
        <LinkedItemComponent
          data={linkedItem}
          parentHierarchyId={hierarchyId}
          index={index}
          isHistory={isHistory}
          isReadOnly={isReadOnly}
          {...options.extraProps?.content}
        />
      );
    }

    if (!entityComponent) {
      throw new Error(`Unknown entity type: ${entity.type}`);
    }

    if (entity.type === EntityType.Module && !isHistory) {
      const module = entity as ModuleContainerUnion;

      const availableLayoutModules = (options.extraProps?.module?.layout?.availableLayoutModules ?? []).map(
        (m) => m.defaultModule.uiModuleType
      );
      const isAvailableLayoutModuleType = availableLayoutModules.includes(
        (module.attributes.pageModule as PageModule).uiModuleType
      );

      let isGroupTreatmentDraggable = false;

      if ('groupId' in module) {
        const draggableGroupTreatments = options.extraProps?.module?.layout?.groupConfigs;
        const { groupId } = module;

        const treatmentType = getTreatmentType(module.attributes.pageModule as PageModule);

        const groupConfig = draggableGroupTreatments?.find((groupConfig) => groupConfig.id === groupId);

        if (groupConfig && Array.isArray(groupConfig.draggableTreatmentTypes) && treatmentType != null) {
          isGroupTreatmentDraggable = groupConfig.draggableTreatmentTypes.includes(treatmentType);
        }
      }

      const fullHierarchyId = generateHierarchyId(entity, hierarchyId, index);

      if (
        !withoutDragNDrop &&
        ((isAvailableLayoutModuleType && root.type !== EntityType.Issue && !isReadOnly) || isGroupTreatmentDraggable)
      ) {
        const currentGroupId = getEntity(fullHierarchyId)?.groupId;

        const previousEntity = getEntity(
          generateHierarchyId(entity, hierarchyId, index - 1)
        ) as ModuleContainerUnion | null;
        let isPreviousAvailableLayoutModuleType = null;
        if (previousEntity) {
          isPreviousAvailableLayoutModuleType = availableLayoutModules.includes(
            (previousEntity.attributes.pageModule as PageModule).uiModuleType
          );
        }

        const nextEntity = getEntity(
          generateHierarchyId(entity, hierarchyId, index + 1)
        ) as ModuleContainerUnion | null;
        let isNextAvailableLayoutModuleType = null;
        if (nextEntity) {
          isNextAvailableLayoutModuleType = availableLayoutModules.includes(
            (nextEntity.attributes.pageModule as PageModule).uiModuleType
          );
        }

        // For modules that are not grouped but still can be dragged (eg. barrons)
        const noGroup = currentGroupId == null;

        const canMoveUp =
          index > 0 && (noGroup ? isPreviousAvailableLayoutModuleType : previousEntity?.groupId === currentGroupId);
        const canMoveDown = noGroup ? isNextAvailableLayoutModuleType : nextEntity?.groupId === currentGroupId;
        const isArrowUpDisabled = !canMoveUp;
        const isArrowDownDisabled = !canMoveDown;

        entityComponent = (
          <Draggable
            key={DRAGGABLE_PREFIXES.MODULE + fullHierarchyId}
            draggableId={DRAGGABLE_PREFIXES.MODULE + fullHierarchyId}
            index={index}
            isDragDisabled={isReadOnly}
          >
            {((currComponent) =>
              // eslint-disable-next-line func-names
              function (draggableProvided) {
                return (
                  <div
                    ref={draggableProvided.innerRef}
                    className={moduleStyles.draggableModule}
                    {...draggableProvided.draggableProps}
                  >
                    {currComponent}
                    <Wrapper padding={{ all: 'sm' }} className={moduleStyles.moduleIconContainer}>
                      <Button
                        tertiary
                        disabled={isArrowUpDisabled || isReadOnly}
                        icon={IconArrowUp}
                        onClick={() => {
                          swapEntities(fullHierarchyId, generateHierarchyId(entity, hierarchyId, index - 1));
                        }}
                      />
                      <div {...draggableProvided.dragHandleProps}>
                        <IconHandle color="asphalt" />
                      </div>
                      <Button
                        tertiary
                        disabled={isArrowDownDisabled || isReadOnly}
                        icon={IconArrowDown}
                        onClick={() => {
                          swapEntities(fullHierarchyId, generateHierarchyId(entity, hierarchyId, index + 1));
                        }}
                      />
                    </Wrapper>
                  </div>
                );
              })(entityComponent)}
          </Draggable>
        );
      }
    }

    // Wrap Article, Collection, and Query entities in Draggable
    if (
      (entity.type === EntityType.Article ||
        entity.type === EntityType.Collection ||
        entity.type === EntityType.Query ||
        entity.type === EntityType.LinkedItem) &&
      !withoutDragNDrop &&
      !isReadOnly
    ) {
      const fullHierarchyId = generateHierarchyId(entity, hierarchyId, index);
      const draggablePrefix = isHistory ? DRAGGABLE_PREFIXES.HISTORY_MODULE_ITEM : DRAGGABLE_PREFIXES.MODULE_ITEM;
      let draggableId = draggablePrefix + fullHierarchyId;
      if (isHistory) {
        draggableId = draggablePrefix + JSON.stringify(entity);
      }

      const isDragDisabled = !!(entity.type === EntityType.Query && (entity as QueryItem).attributes.isFeed);

      entityComponent = (
        <Draggable draggableId={draggableId} index={index} isDragDisabled={isDragDisabled}>
          {((currComponent) =>
            // eslint-disable-next-line func-names
            function (draggableProvided) {
              return (
                <div
                  ref={draggableProvided.innerRef}
                  {...draggableProvided.draggableProps}
                  {...draggableProvided.dragHandleProps}
                >
                  {currComponent}
                </div>
              );
            })(entityComponent)}
        </Draggable>
      );
    }

    return entityComponent;
  }

  function countContent(entity: EntityComponent): string {
    const { articles, collectionItems, queryItems, feedItems } = getTotalContentItemCounts(entity, {
      skipDisabledGroups: true
    });

    return `${articles} item${articles > 1 ? 's' : ''}${
      collectionItems ? `, ${collectionItems} collection${collectionItems > 1 ? 's' : ''}` : ''
    }${queryItems ? `, ${queryItems} quer${queryItems > 1 ? 'ies' : 'y'}` : ''}${
      feedItems ? `, ${feedItems} feed${feedItems > 1 ? 's' : ''} ` : ''
    }`;
  }

  function getAllModuleCounts(): Record<string, number> {
    const counts: Record<string, number> = {};

    traverseEntity(auxRoot.current, (entity) => {
      if (entity.type === EntityType.Module) {
        const module = entity as ModuleContainer;
        if (module.attributes.pageModule) {
          const treatmentType = getTreatmentType(module.attributes.pageModule);
          if (treatmentType) {
            counts[treatmentType] = (counts[treatmentType] || 0) + 1;
          }
        }
      }
    });

    return counts;
  }

  useLayoutEffect(() => {
    if (isQueryLoading) {
      setQueriesHaveSettled(false);
      return () => {};
    }

    // Wait 2.5 seconds after the last query loading to set queriesHaveSettled to true
    const timeoutRef = setTimeout(() => {
      setQueriesHaveSettled(true);
    }, 2500);

    return () => {
      clearTimeout(timeoutRef);
    };
  }, [isQueryLoading]);

  useEffect(() => {
    auxRoot.current = processedRoot;
    setRoot(processedRoot);
    setLastSavedRoot(processedRoot);
    resetModelChanged();
  }, [processedRoot]);

  useEffect(() => {
    setMetadata(initialMetadata);
    setLastSavedMetadata(initialMetadata);
    resetModelChanged();
  }, [initialMetadata]);

  useEffect(() => {
    setArticlePromotionLog(initialArticlePromotionLog);
    resetModelChanged();
  }, [initialArticlePromotionLog]);

  useEffect(() => {
    const modifiedMetadata = cloneDeep(metadata);
    const modifiedLastSavedMetadata = cloneDeep(lastSavedMetadata);
    delete modifiedMetadata.publishUtc;
    delete modifiedMetadata.updatedUtc;
    delete modifiedLastSavedMetadata.publishUtc;
    delete modifiedLastSavedMetadata.updatedUtc;

    // Consider data model modified if the root or metadata has changed from last saved state
    // Uses custom comparator to ignore fields which are altered during page load (limit, count, contentItems)
    const hasDataModelBeenModified =
      !isEqualWith(root, lastSavedRoot, isEqualIgnoringVolatileFields) ||
      !isEqual(modifiedMetadata, modifiedLastSavedMetadata);

    if (hasDataModelBeenModified) {
      setHasModelChangedRecently(true);
      setHasModelChangedOnce(true);

      if (type === DataModelType.ItpDTO && setHasItpDTOChanged) {
        setHasItpDTOChanged(true);
      }
    } else {
      setHasModelChangedRecently(false);
    }
  }, [root, metadata, lastSavedRoot, lastSavedMetadata, setHasItpDTOChanged, type]);

  useEffect(
    () => () => {
      resetState();
    },
    [resetState]
  );

  const getRootCopy = () => cloneDeep(auxRoot.current);

  // stableValue only recomputes when generateHierarchyId or getArticlePromotionLogByOriginId
  // change identity — which they don't on drag end — keeping DataModelStableContext's value
  // reference-equal and preventing its subscribers from re-rendering.
  const stableValue = useMemo(
    () => ({ generateHierarchyId, getArticlePromotionLogByOriginId }),
    [generateHierarchyId, getArticlePromotionLogByOriginId]
  );

  const value = {
    type,
    root,
    metadata,
    hasModelChangedOnce,
    hasModelChangedRecently,
    setHasModelChangedRecently,
    resetModelChanged,
    readHierarchyId,
    getAllArticleIds,
    getAllExternalCollectionIds,
    getDTO,
    getRefDTO,
    deleteResolvedFieldsFromExternalDTO,
    fromAllessehContent,
    generateHierarchyId,
    generateHierarchyIdFromDragEvent,
    setNewRoot,
    applyDedupedRoot,
    setNewMetadata,
    getEntity,
    swapEntities,
    insertEntity,
    insertEntityToCollection,
    modifyEntity,
    clearModule,
    modifyAllByType,
    applyOverfetchForModifiedQuery,
    applyOverfetchForAddedQuery,
    changeEntity,
    removeEntity,
    removeAllByType,
    cleanupDedupeForGroup,
    getEmbargoContent,
    getCancelledEmbargoContent,
    getSortedEmbargoContent,
    renderEntity,
    isEditingLinkedItem,
    setIsEditingLinkedItem,
    isItemInDataModel,
    countContent,
    getAllModuleCounts,
    traverseEntity,
    setIsQueryLoading,
    queriesHaveSettled,
    getRootCopy,
    setHasModelChangedOnce,
    getArticlePromotionLogByOriginId,
    getSectionAttributes,
    setSectionAttributes,
    updateLastSavedState,
    getGroupConfig,
    getAllGroupConfigs,
    isReadOnly,
    validations
  };

  return (
    <DataModelContext.Provider value={value}>
      <DataModelStableContext.Provider value={stableValue}>{children}</DataModelStableContext.Provider>
    </DataModelContext.Provider>
  );
};
